import {int, isDefined, Nullable, Optional, Subscription, UUID} from "@opendaw/lib-std"
import {Box} from "@opendaw/lib-box"
import {EffectBox, EffectFactories, EffectFactory, Project} from "@opendaw/studio-core"
import {AudioCompositeAdapter, AudioEffectCompositeCellBoxAdapter} from "@opendaw/studio-adapters"
import {AudioEffectCompositeCellBox} from "@opendaw/studio-boxes"
import {DragAndDrop} from "@/ui/DragAndDrop"
import {AnyDragData} from "@/ui/AnyDragData"
import {CompositeRows} from "@/ui/devices/CompositeRows"

// All drag & drop of an AudioComposite's entries in one place:
//   - REORDER: drag a branch by its label handle onto another to move it (order is the user's arrangement; the
//     branches sum in parallel, so it does not change the sound).
//   - NEW EFFECT: drop an audio effect from the browser. Onto the MIDDLE of a branch adds it to that branch's
//     serial chain; onto a branch's top / bottom edge (or the Add Effect footer) creates a NEW branch holding
//     just that effect.
// A row is both a reorder target and a new-effect target — only one drag type is ever active, so they share the
// insertion-line / highlight marks.
export namespace AudioCompositeEntryDnD {
    type HandleConstruct = {
        handle: HTMLElement
        classReceiver: HTMLElement
        composite: AudioCompositeAdapter
        uuid: UUID.Bytes
        getIndex: () => int
    }

    // The reorder DRAG SOURCE (the branch label): a handle, so the row's knobs keep their own pointer dragging.
    export const installHandle = ({handle, classReceiver, composite, uuid, getIndex}: HandleConstruct): Subscription =>
        DragAndDrop.installSource(handle, () => ({
            type: "composite-entry",
            uuid: UUID.toString(uuid),
            index: getIndex(),
            composite: UUID.toString(composite.uuid)
        } satisfies AnyDragData), classReceiver)

    type TargetConstruct = {
        element: HTMLElement
        project: Project
        composite: AudioCompositeAdapter
        entry: AudioEffectCompositeCellBoxAdapter
        getIndex: () => int
        // A SPLIT owns its branches (mapped BY INDEX), so it allows neither reorder nor new branches: an effect
        // dropped on any of its rows always goes INTO that branch's chain.
        branchable: boolean
    }

    // A branch row as a drop target for BOTH a reorder and a new effect (see the namespace note).
    export const installTarget = ({element, project, composite, entry, getIndex, branchable}: TargetConstruct): Subscription =>
        DragAndDrop.installTarget(element, {
            drag: (event: DragEvent, data: AnyDragData): boolean => {
                if (data.type === "composite-entry") {
                    if (!branchable || data.composite !== UUID.toString(composite.uuid) || data.index === getIndex()) {
                        return false
                    }
                    // A downward move lands AFTER the target, an upward move BEFORE it.
                    CompositeRows.mark(element, data.index < getIndex() ? "insert-after" : "insert-before")
                    return true
                }
                if (isNewAudioEffect(data)) {
                    const zone = branchable ? CompositeRows.zoneOf(event, element) : "onto"
                    CompositeRows.mark(element, zone === "before" ? "insert-before" : zone === "after" ? "insert-after" : "drop-target")
                    return true
                }
                if (isExistingAudioEffect(data) && acceptsExistingEffect(project, composite, data)) {
                    const zone = branchable ? CompositeRows.zoneOf(event, element) : "onto"
                    CompositeRows.mark(element, zone === "before" ? "insert-before" : zone === "after" ? "insert-after" : "drop-target")
                    return true
                }
                return false
            },
            drop: (event: DragEvent, data: AnyDragData): void => {
                CompositeRows.mark(element, null)
                if (data.type === "composite-entry") {
                    if (!branchable) {return}
                    event.preventDefault()
                    reorder(project, composite, data.index, getIndex())
                } else if (isNewAudioEffect(data)) {
                    event.preventDefault()
                    const factory = EffectFactories.MergedNamed[data.device]
                    if (!isDefined(factory)) {return}
                    const zone = branchable ? CompositeRows.zoneOf(event, element) : "onto"
                    if (zone === "onto") {
                        // Append to THIS branch's serial chain, as the device panel appends to a chain.
                        project.editing.modify(() => project.api.insertEffect(entry.box.audioEffects, factory,
                            entry.box.audioEffects.pointerHub.incoming().length))
                    } else {
                        insertBranch(project, composite, zone === "before" ? getIndex() : getIndex() + 1, factory)
                    }
                } else if (isExistingAudioEffect(data) && acceptsExistingEffect(project, composite, data)) {
                    event.preventDefault()
                    const boxes = CompositeRows.resolveEffectBoxes(project, data.uuids, "audio-effect")
                    const zone = branchable ? CompositeRows.zoneOf(event, element) : "onto"
                    const copy = DragAndDrop.isCopy(event, data)
                    if (zone === "onto") {
                        // MOVE the dragged effects into THIS branch's serial chain (re-homing them out of their source).
                        const insertIndex = entry.box.audioEffects.pointerHub.incoming().length
                        project.editing.modify(() => copy
                            ? project.api.copyEffects(entry.box.audioEffects, boxes, insertIndex)
                            : project.api.moveEffects(entry.box.audioEffects, boxes, insertIndex))
                    } else {
                        moveToNewBranch(project, composite, zone === "before" ? getIndex() : getIndex() + 1, boxes, copy)
                    }
                }
            },
            enter: () => {},
            leave: () => CompositeRows.mark(element, null)
        })

    type AppendConstruct = {
        element: HTMLElement
        project: Project
        composite: AudioCompositeAdapter
    }

    // The whole entry list appends a branch, except over a row, which is that row's own target.
    export const installAppendTarget = ({element, project, composite}: AppendConstruct): Subscription =>
        DragAndDrop.installTarget(element, {
            drag: (event: DragEvent, data: AnyDragData): boolean => {
                const accepts = !CompositeRows.overRow(event)
                    && (isNewAudioEffect(data) || acceptsExistingEffect(project, composite, data))
                element.classList.toggle("insert-append", accepts)
                return accepts
            },
            drop: (event: DragEvent, data: AnyDragData): void => {
                element.classList.remove("insert-append")
                if (CompositeRows.overRow(event)) {return}
                const atIndex = composite.entries.adapters().length
                if (isNewAudioEffect(data)) {
                    const factory = EffectFactories.MergedNamed[data.device]
                    if (!isDefined(factory)) {return}
                    event.preventDefault()
                    insertBranch(project, composite, atIndex, factory)
                } else if (isExistingAudioEffect(data) && acceptsExistingEffect(project, composite, data)) {
                    event.preventDefault()
                    moveToNewBranch(project, composite, atIndex,
                        CompositeRows.resolveEffectBoxes(project, data.uuids, "audio-effect"), DragAndDrop.isCopy(event, data))
                }
            },
            enter: () => {},
            leave: () => element.classList.remove("insert-append")
        })

    // Create a new branch at `atIndex` holding just `factory`, shifting the branches at or after it down by one.
    export const insertBranch = (project: Project, composite: AudioCompositeAdapter,
                                 atIndex: int, factory: EffectFactory): void => {
        project.editing.modify(() => {
            composite.entries.adapters()
                .filter(other => other.indexField.getValue() >= atIndex)
                .forEach(other => other.indexField.setValue(other.indexField.getValue() + 1))
            const cell = AudioEffectCompositeCellBox.create(project.boxGraph, UUID.generate(), box => {
                box.composite.refer(composite.box.entries)
                box.index.setValue(atIndex)
            })
            project.api.insertEffect(cell.audioEffects, factory, 0)
        })
    }

    const isNewAudioEffect = (data: AnyDragData): data is Extract<AnyDragData, { type: "audio-effect" }> & { uuids: null } =>
        data.type === "audio-effect" && data.uuids === null

    // An EXISTING audio effect (or several) being dragged out of a chain to be MOVED, as opposed to a new one
    // created from the browser (`uuids === null`).
    type ExistingAudioEffectDrag = { type: "audio-effect", uuids: ReadonlyArray<UUID.String>, instrument: Nullable<UUID.String> }
    const isExistingAudioEffect = (data: AnyDragData): data is ExistingAudioEffectDrag =>
        data.type === "audio-effect" && data.uuids !== null

    // Walk up from a box through its host chain (an effect's `host`, a composite entry's `composite`) to its
    // owner. Stops at the audio unit (which has no such pointer).
    const ancestorOf = (box: Box): Optional<Box> => {
        if (box instanceof AudioEffectCompositeCellBox) {
            return box.composite.targetVertex.unwrapOrNull()?.box
        }
        if (box.tags.deviceType === "audio-effect" || box.tags.deviceType === "midi-effect") {
            return (box as EffectBox).host.targetVertex.unwrapOrNull()?.box
        }
        return undefined
    }

    // Dropping a box into a branch of a composite that lives inside that box's OWN subtree would be a cycle
    // (e.g. dragging a composite into one of its own branches). Reject it by walking the target composite's
    // ancestry and looking for any dragged box.
    const wouldCycle = (draggedUuids: ReadonlySet<UUID.String>, composite: AudioCompositeAdapter): boolean => {
        let current: Optional<Box> = composite.box
        while (isDefined(current)) {
            if (draggedUuids.has(UUID.toString(current.address.uuid))) {return true}
            current = ancestorOf(current)
        }
        return false
    }

    const acceptsExistingEffect = (project: Project, composite: AudioCompositeAdapter, data: AnyDragData): boolean =>
        isExistingAudioEffect(data)
        && CompositeRows.resolveEffectBoxes(project, data.uuids, "audio-effect").length > 0
        && !wouldCycle(new Set(data.uuids), composite)

    // Move existing effect boxes into a NEW branch created at `atIndex`, shifting the branches at or after it down.
    const moveToNewBranch = (project: Project, composite: AudioCompositeAdapter,
                             atIndex: int, boxes: ReadonlyArray<EffectBox>, copy: boolean = false): void => {
        project.editing.modify(() => {
            composite.entries.adapters()
                .filter(other => other.indexField.getValue() >= atIndex)
                .forEach(other => other.indexField.setValue(other.indexField.getValue() + 1))
            const cell = AudioEffectCompositeCellBox.create(project.boxGraph, UUID.generate(), box => {
                box.composite.refer(composite.box.entries)
                box.index.setValue(atIndex)
            })
            if (copy) {
                project.api.copyEffects(cell.audioEffects, boxes, 0)
            } else {
                project.api.moveEffects(cell.audioEffects, boxes, 0)
            }
        })
    }

    const reorder = (project: Project, composite: AudioCompositeAdapter, fromIndex: int, toIndex: int): void => {
        const ordered = composite.entries.adapters()
            .toSorted((left, right) => left.indexField.getValue() - right.indexField.getValue())
        const from = ordered.findIndex(entry => entry.indexField.getValue() === fromIndex)
        const to = ordered.findIndex(entry => entry.indexField.getValue() === toIndex)
        if (from < 0 || to < 0 || from === to) {return}
        const moved = ordered[from]
        ordered.splice(from, 1)
        ordered.splice(to, 0, moved)
        project.editing.modify(() => ordered.forEach((entry, index) => entry.indexField.setValue(index)))
    }
}
